import { useState, useCallback, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useTranslation } from 'react-i18next';
import { useMap } from '../hooks/useMap';
import { usePins } from '../hooks/usePins';
import { useMapPins } from '../hooks/useMapPins';
import { LocationSearchBar } from './LocationSearchBar';
import { AddPinDialog } from './AddPinDialog';
import { PinPopup } from './PinPopup';
import { MapStyleSwitcher } from './MapStyleSwitcher';
import type { Pin } from '../types';

/**
 * Main interactive map component integrating all map features:
 * - Map lifecycle (useMap)
 * - Pin data (usePins)
 * - Pin rendering with clustering (useMapPins)
 * - Location search
 * - Add pin dialog (opens directly on map click)
 * - Pin popup/timeline
 * - Map style switcher
 */
export function InteractiveMap() {
  const { t } = useTranslation();
  const { map, mapContainerRef, isLoaded, style, setStyle, flyTo, userLocation } = useMap();
  const { pins, refresh, selectPin, selectedPin } = usePins();
  const [addPinCoords, setAddPinCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addPinTitle, setAddPinTitle] = useState<string | undefined>(undefined);
  const [showPopup, setShowPopup] = useState(false);
  const tempMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const pinClickedRef = useRef(false);

  // Handle pin click from useMapPins
  const handlePinClick = useCallback((pin: Pin) => {
    pinClickedRef.current = true;
    selectPin(pin);
    // Close add-pin dialog if open (priority to existing pin)
    setAddPinCoords(null);
    setAddPinTitle(undefined);
    if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }
    setShowPopup(true);
    flyTo([pin.longitude, pin.latitude]);
  }, [selectPin, flyTo]);

  // Render pins on map
  useMapPins({ map, pins, isLoaded, onPinClick: handlePinClick });

  // Direct map click -> open check-in dialog (no add-pin mode needed)
  useEffect(() => {
    if (!map || !isLoaded) return;

    const handleMapClick = (e: any) => {
      // If PinPopup is showing, don't open AddPinDialog
      if (showPopup) {
        setShowPopup(false);
        selectPin(null);
        return;
      }
      // If a pin was just clicked, skip (pinClickedRef is set in handlePinClick)
      if (pinClickedRef.current) {
        pinClickedRef.current = false;
        return;
      }

      // If popup is showing, close it first instead of opening dialog
      if (showPopup) {
        setShowPopup(false);
        selectPin(null);
        return;
      }

      // If dialog is already open, close it
      if (addPinCoords) {
        setAddPinCoords(null);
        setAddPinTitle(undefined);
        if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }
        return;
      }

      // Remove old temp marker
      if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }

      // Query ALL rendered features at click point to detect POIs (restaurants, shops, etc.)
      // We query a small bounding box around the click for better hit detection
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - 5, e.point.y - 5],
        [e.point.x + 5, e.point.y + 5]
      ];
      const allFeatures = map.queryRenderedFeatures(bbox);
      let clickTitle: string | undefined = undefined;
      // Find the first feature that looks like a POI (has a name and is a symbol/label)
      const poiFeature = allFeatures.find((f: any) =>
        f.properties?.name && f.sourceLayer &&
        (f.sourceLayer.includes('poi') || f.sourceLayer.includes('label') || f.sourceLayer.includes('place'))
        && f.layer?.type === 'symbol'
      );
      if (poiFeature) {
        clickTitle = poiFeature.properties.name;
      }

      // Add temporary marker at clicked location (green drop pin)
      const marker = new mapboxgl.Marker({ color: '#2e7d32', scale: 1.3 }).setLngLat([e.lngLat.lng, e.lngLat.lat]).addTo(map);
      tempMarkerRef.current = marker;

      // Open check-in dialog directly with clicked coordinates
      setAddPinCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setAddPinTitle(clickTitle);
    };

    const handleDrag = () => {
      if (showPopup) {
        setShowPopup(false);
        selectPin(null);
      }
    };

    map.on('click', handleMapClick);
    map.on('dragstart', handleDrag);
    return () => {
      map.off('click', handleMapClick);
      map.off('dragstart', handleDrag);
    };
  }, [map, isLoaded, showPopup, selectPin, addPinCoords]);

  // Handle search location select
  const handleLocationSelect = useCallback((center: [number, number], placeName: string) => {
    // Remove old temp marker
    if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }
    // Add temporary marker at selected location (larger, green)
    if (map) {
      const marker = new mapboxgl.Marker({ color: '#2e7d32', scale: 1.3 }).setLngLat(center).addTo(map);
      tempMarkerRef.current = marker;
      // Fly to location then pan left so marker is visible alongside right panel
      flyTo(center);
      setTimeout(() => { if (map) map.panBy([150, 0], { duration: 500 }); }, 2000);
    }
    setAddPinCoords({ lat: center[1], lng: center[0] });
    setAddPinTitle(placeName);
  }, [flyTo, map]);

  // FAB: fly to user's current location
  const handleMyLocation = useCallback(() => {
    if (userLocation) {
      flyTo([userLocation[0], userLocation[1]]);
    } else {
      navigator.geolocation.getCurrentPosition(
        (pos) => flyTo([pos.coords.longitude, pos.coords.latitude]),
        () => {}
      );
    }
  }, [flyTo, userLocation]);

  // Handle dialog close
  const handleDialogClose = useCallback(() => {
    setAddPinCoords(null);
    setAddPinTitle(undefined);
    if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }
  }, []);

  // Handle pin creation success
  const handlePinSuccess = useCallback(() => {
    refresh();
    setAddPinCoords(null);
    setAddPinTitle(undefined);
    if (tempMarkerRef.current) { tempMarkerRef.current.remove(); tempMarkerRef.current = null; }
  }, [refresh]);

  // Close popup
  const handlePopupClose = useCallback(() => {
    setShowPopup(false);
    selectPin(null);
  }, [selectPin]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 'calc(100vh - 120px)' }}>
      {/* Map container */}
      <Box
        ref={mapContainerRef}
        sx={{ position: 'absolute', inset: 0 }}
      />

      {/* Search bar */}
      <LocationSearchBar
        onLocationSelect={handleLocationSelect}
        userLocation={userLocation}
      />

      {/* Style switcher */}
      <MapStyleSwitcher currentStyle={style} onStyleChange={setStyle} />

      
      {/* Add Pin Panel (right side) */}
      {addPinCoords && (
        <Box sx={{ position: 'absolute', top: 16, right: 0, bottom: 80, width: { xs: '85%', sm: 360 }, zIndex: 10, maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}>
          <AddPinDialog
            open={!!addPinCoords}
            latitude={addPinCoords.lat}
            longitude={addPinCoords.lng}
            onClose={handleDialogClose}
            onSuccess={handlePinSuccess}
            initialTitle={addPinTitle}
          />
        </Box>
      )}

      {/* Pin Popup */}
      {showPopup && selectedPin && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 60,
            zIndex: 10,
          }}
        >
          <PinPopup
            pin={selectedPin}
            onClose={handlePopupClose}
            onPhotosAdded={refresh}
          />
        </Box>
      )}
    </Box>
  );
}